import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { getInspectorConfig } from '../../../services/inspectorApi';
import InspectorSetup from './InspectorSetup';
import InspectorDashboard from './InspectorDashboard';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState, PortalSectionHeader } from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { CK, FONT_BODY, sectionCardSx } from '../portalStyles';

// Must match `color` in engines/inspector.js — the sidebar, the header avatar and
// the wizard's buttons all claim to be "the Inspector purple".
const INSPECTOR_COLOR = '#7D3C98';

// Consecutive background-poll failures tolerated before the error card replaces
// the progress view. Matches the Inspector dashboard's counter, so at the shared
// 3s cadence a hard fault surfaces after roughly nine seconds.
const MAX_POLL_FAILURES = 3;

// Every Inspector state (loading, error, transitioning) keeps the same page
// header mounted so the layout never jumps as the config lands.
const inspectorHeader = (
  <PortalPageHeader
    icon={<BugReportOutlinedIcon sx={{ fontSize: 24 }} />}
    title="Inspector"
    subtitle="Amazon Inspector vulnerability management"
    color={INSPECTOR_COLOR}
  />
);

// transitionCopy picks the in-progress wording from the config's progressLabel.
//
// progressLabel is the ONLY reliable discriminator between the three fan-outs.
// There is no "disabling" status: DisableInspector flips the row to in_progress
// and stamps "Disabling" in the same UPDATE, and claimConfigForUpdate stamps
// "Updating" the same way — both atomic with the status change. errorMessage
// (which the GuardDuty/Macie entries key off) is useless here: the disable path
// never writes it and the add-member/retry paths clear it.
//
// Enable is the fallback rather than a matched case because it is the one path
// that can briefly show a stale or empty label: RetryMember and AddMembers set
// the status before their goroutine gets around to stamping "Granting
// permissions" / "Enabling Amazon Inspector".
function transitionCopy(progressLabel) {
  const hint = (progressLabel || '').toLowerCase();
  if (hint.includes('disabl')) {
    return {
      title: 'Disabling Amazon Inspector…',
      body: 'Turning off Inspector in every enrolled account and region and detaching the scoped role permissions Prism attached. This can take a few minutes.',
    };
  }
  if (hint.includes('updat')) {
    return {
      title: 'Updating Amazon Inspector…',
      body: 'Applying the requested regions and scan types across every enrolled account. This can take a few minutes.',
    };
  }
  return {
    title: 'Enabling Amazon Inspector across accounts and regions…',
    body: 'Granting scoped permissions and turning on Inspector in each account. Accounts are enrolled independently, so results appear as each one finishes.',
  };
}

// InspectorEntry decides what to render at /securityhub/inspector. Flow:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   enabled / partial_failure      → Inspector dashboard
//
// partial_failure deliberately routes to the dashboard, NOT back to the wizard.
// In the flat peer model every account is enrolled on its own and one account's
// failure never blocks the rest, so partial_failure means "most of this is live
// and scanning" — the per-member Retry and the Fix-roles flow on the Enrollment
// page are the repair path, and re-running the wizard would tear the working
// accounts down and start over.
//
// The "in_progress" branch covers every fan-out that can be started from
// elsewhere in the UI — initial enable, add/retry member, a region or scan-type
// change, and teardown from Settings → Danger Zone — because all of them route
// here and let this component's polling drive the page forward.
export default function InspectorEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getInspectorConfig();
      // A tenant that has never enabled Inspector resolves with data: null. That
      // is the wizard signal, not a fault — only a rejection is an error.
      setConfig(res?.data || null);
      pollFailuresRef.current = 0;
      setError(null);
    } catch (e) {
      // The error card is checked BEFORE the in_progress branch, so reporting
      // every rejection would let one blipped poll replace a multi-minute enable
      // or teardown narration with a red failure — during exactly the operation
      // this screen exists to narrate. A user-visible load has nothing to fall
      // back on and reports at once; a background poll has to fail repeatedly
      // first. Staying silent forever is the opposite lie (a dead backend would
      // render as a progress bar that is still "working"), so a sustained
      // outage does eventually surface, and the next successful tick clears it.
      if (showSpinner) {
        setError(e.message);
      } else {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) setError(e.message);
      }
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition so the view advances by itself once
  // the orchestrator goroutine finishes. Depending on the status alone (not the
  // whole config) keeps the interval from being torn down and rebuilt on every
  // tick, which would reset the cadence each time the progress percentage moves.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    // Each transition starts with a clean slate — failures tolerated during an
    // earlier operation must not count against this one.
    pollFailuresRef.current = 0;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{inspectorHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Inspector…" color={INSPECTOR_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{inspectorHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const { title, body } = transitionCopy(config.progressLabel);
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{inspectorHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<BugReportOutlinedIcon sx={{ fontSize: 20 }} />}
              title={title}
              color={INSPECTOR_COLOR}
              mb={1.5}
            />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {body}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "nothing is enrolled" states — surface the setup wizard so the user
  // can re-run. EnableInspector drops the settled prior config and its children
  // server-side before creating a new one, so submitting again is safe.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <InspectorSetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <InspectorDashboard config={config} onConfigChange={refresh} />;
}
